import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ExternalLink, ListPlus, Pencil, Plus, Trash2 } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useSite } from "@/context/SiteContext";

export default function Pages() {
  const nav = useNavigate();
  const { menu, reload } = useSite();
  const [pages, setPages] = useState([]);
  const [title, setTitle] = useState("");

  const load = useCallback(async () => {
    const { data } = await api.get("/admin/pages");
    setPages(data);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const create = async (e) => {
    e.preventDefault();
    if (!title.trim()) return;
    try {
      const { data } = await api.post("/admin/pages", { title, sections: [] });
      nav(`/admin/pages/${data.id}`);
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const del = async (p) => {
    if (!window.confirm(`Delete page "${p.title}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/pages/${p.id}`);
      toast.success("Page deleted");
      load();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const addToMenu = async (p) => {
    const url = `/${p.slug}`;
    if (menu.some((m) => m.url === url || m.children?.some((c) => c.url === url))) return toast.info("This page is already in the menu");
    try {
      await api.put("/admin/menu", { items: [...menu, { id: `page-${p.slug}`, label: p.title, type: "link", url, visible: true, children: [] }] });
      await reload();
      toast.success("Added to the menu – reorder it in Menu Manager");
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  return (
    <div data-testid="admin-pages">
      <h1 className="text-3xl font-bold">Pages</h1>
      <p className="text-sm text-stone-500 mt-1 mb-6">Create new pages from sections, save as draft and publish when ready.</p>
      <form onSubmit={create} className="flex gap-2 mb-6 max-w-lg">
        <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="New page title, e.g. Travel Partners" className="flex-1 border border-stone-300 bg-white rounded-md px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-crimson/30" data-testid="page-new-title" />
        <button className="inline-flex items-center gap-1.5 bg-crimson hover:bg-crimson-hover text-white rounded-md px-4 text-sm font-bold" data-testid="page-create-btn"><Plus className="w-4 h-4" />New Page</button>
      </form>
      <div className="bg-white border border-stone-200 rounded-lg overflow-x-auto">
        <table className="w-full min-w-[640px]" data-testid="pages-table">
          <thead className="bg-stone-50 text-left text-sm text-stone-500">
            <tr><th className="px-5 py-3 font-normal">Title</th><th className="px-5 py-3 font-normal">Address</th><th className="px-5 py-3 font-normal">Status</th><th className="px-5 py-3 font-normal text-right">Actions</th></tr>
          </thead>
          <tbody>
            {pages.map((p) => (
              <tr key={p.id} className="border-t border-stone-100" data-testid={`page-row-${p.slug}`}>
                <td className="px-5 py-3 text-sm font-bold">{p.title}</td>
                <td className="px-5 py-3 text-sm text-stone-500">/{p.slug}</td>
                <td className="px-5 py-3"><span className={`text-xs font-bold px-2.5 py-1 rounded-full ${p.status === "published" ? "bg-green-100 text-green-800" : "bg-amber-100 text-amber-800"}`}>{p.status === "published" ? "Published" : "Draft"}</span></td>
                <td className="px-5 py-3">
                  <div className="flex justify-end gap-1">
                    <a href={`/${p.slug}`} target="_blank" rel="noreferrer" className="p-2 text-stone-500 hover:text-charcoal" title="View" data-testid={`page-view-${p.slug}`}><ExternalLink className="w-4 h-4" /></a>
                    <button onClick={() => addToMenu(p)} className="p-2 text-stone-500 hover:text-charcoal" title="Add to menu" data-testid={`page-add-menu-${p.slug}`}><ListPlus className="w-4 h-4" /></button>
                    <Link to={`/admin/pages/${p.id}`} className="p-2 text-stone-500 hover:text-charcoal" title="Edit" data-testid={`page-edit-${p.slug}`}><Pencil className="w-4 h-4" /></Link>
                    <button onClick={() => del(p)} className="p-2 text-red-500 hover:text-red-700" title="Delete" data-testid={`page-delete-${p.slug}`}><Trash2 className="w-4 h-4" /></button>
                  </div>
                </td>
              </tr>
            ))}
            {!pages.length && <tr><td colSpan={4} className="text-center text-sm text-stone-400 py-10" data-testid="pages-empty">No pages yet – create your first one above.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
